import { DEFAULT_LOGO_URL } from '@seek/libs-shared';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Heading,
  IconArrow,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import { ExperimentTwoProgress } from '../../type';
import translations from './.vocab';
import * as styles from './ReviewPageLayout.css';

type ReviewPageLayoutProps = React.PropsWithChildren<{
  heading?: string;
  hideStepper?: boolean;
  isContinueLoading?: boolean;
  logo?: string;
  onContinue?: () => void;
  onSkip?: () => void;
  progress?: ExperimentTwoProgress;
  subtitle?: string;
  testid?: string;
}>;

interface DesktopButtonProps {
  isContinueLoading: boolean;
  enableSkip?: boolean;
  onContinue?: () => void;
  onSkip?: () => void;
}

const DesktopButton = ({
  onSkip,
  onContinue,
  enableSkip,
  isContinueLoading,
}: DesktopButtonProps) => {
  return (
    <Inline space="small" align="right" collapseBelow="tablet">
      {enableSkip && (
        <Button
          tone="formAccent"
          variant="transparent"
          icon={<IconArrow direction="right" />}
          iconPosition="trailing"
          onClick={onSkip}
          data={{ testid: 'skip-button' }}
        >
          Skip question
        </Button>
      )}
      {onContinue && (
        <Button
          variant="solid"
          tone="formAccent"
          onClick={onContinue}
          data={{ testid: 'save-and-continue-button' }}
          loading={isContinueLoading}
        >
          Save and continue
        </Button>
      )}
    </Inline>
  );
};

export const ReviewPageLayoutNoStepper = ({
  progress = 1,
  heading,
  subtitle,
  logo,
  children,
  testid,
  onContinue,
  onSkip,
  isContinueLoading = false,
  hideStepper = false,
}: ReviewPageLayoutProps) => {
  const { t } = useTranslations(translations);

  return (
    <Box
      data={testid ? { testid } : {}}
      paddingTop={{
        tablet: 'large',
        mobile: 'medium',
      }}
    >
      <Stack space="medium">
        <Stack space="xlarge">
          <Stack space="large">
            {logo && logo !== DEFAULT_LOGO_URL ? (
              <Box
                component="img"
                src={logo}
                alt={t('company logo')}
                className={styles.logoBox}
              />
            ) : null}
            {!hideStepper && (
              <Text align="right" tone="secondary">
                {progress - 2} of 7
              </Text>
            )}
            {heading ? <Heading level="1">{heading}</Heading> : null}
            {subtitle ? (
              <Text size="standard" tone="secondary">
                {subtitle}
              </Text>
            ) : null}
          </Stack>
          <Box className={styles.questionsBox}>{children}</Box>
        </Stack>
        {progress === ExperimentTwoProgress.YourRole && (
          <Inline space="none" align="right" collapseBelow="tablet">
            <Button
              tone="brandAccent"
              variant="solid"
              onClick={onContinue}
              loading={isContinueLoading}
            >
              Submit
            </Button>
          </Inline>
        )}
        {progress !== ExperimentTwoProgress.YourRole && (
          <DesktopButton
            isContinueLoading={isContinueLoading}
            enableSkip={progress !== ExperimentTwoProgress.OverallRating}
            onSkip={onSkip}
            onContinue={onContinue}
          />
        )}
      </Stack>
    </Box>
  );
};
